'use client';

import { useEffect, useState } from 'react';
import { Heart, Share2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { nativeShare } from '@/lib/share';

interface ServiceHeaderActionsProps {
  serviceId?: number;
  serviceName?: string;
  /** Called when an action needs the user to be logged in. */
  onRequireLogin: () => void;
}

/**
 * Favourite + Share actions shown in the top nav on a service page (native
 * app only), in place of the search icon and menu. Favourite toggles via the
 * existing /api/consumer/favourites endpoint; Share opens the native share
 * sheet (WhatsApp, Facebook, etc.) for the current service URL.
 */
export function ServiceHeaderActions({
  serviceId,
  serviceName,
  onRequireLogin,
}: ServiceHeaderActionsProps) {
  const { isAuthenticated } = useAuth();
  const [isFavourite, setIsFavourite] = useState(false);
  const [busy, setBusy] = useState(false);

  // Load whether this service is already favourited.
  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated || !serviceId) {
      setIsFavourite(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch('/api/consumer/favourites');
        const data = await res.json();
        if (!cancelled && data?.success && Array.isArray(data.data)) {
          setIsFavourite(
            data.data.some((f: { serviceId: number }) => Number(f.serviceId) === Number(serviceId))
          );
        }
      } catch {
        // Ignore — leave as not-favourited.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, serviceId]);

  const toggleFavourite = async () => {
    if (!isAuthenticated) {
      onRequireLogin();
      return;
    }
    if (!serviceId || busy) return;

    const next = !isFavourite;
    setBusy(true);
    setIsFavourite(next); // optimistic
    try {
      const res = next
        ? await fetch('/api/consumer/favourites', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ serviceId }),
          })
        : await fetch(`/api/consumer/favourites?serviceId=${serviceId}`, {
            method: 'DELETE',
          });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        setIsFavourite(!next); // revert
        if (res.status === 401) onRequireLogin();
      }
    } catch {
      setIsFavourite(!next); // revert
    } finally {
      setBusy(false);
    }
  };

  const handleShare = () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (!url) return;
    const title = serviceName ? `${serviceName} — DocuPro` : 'DocuPro';
    void nativeShare({ title, text: title, url });
  };

  return (
    <>
      <button
        type="button"
        onClick={toggleFavourite}
        aria-pressed={isFavourite}
        aria-label={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
        className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-muted transition-colors active:opacity-60"
      >
        <Heart
          className={cn(
            'w-5 h-5 transition-colors',
            isFavourite ? 'fill-red-500 text-red-500' : 'text-muted-foreground'
          )}
        />
      </button>
      <button
        type="button"
        onClick={handleShare}
        aria-label="Share"
        className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-muted transition-colors active:opacity-60"
      >
        <Share2 className="w-5 h-5 text-muted-foreground" />
      </button>
    </>
  );
}
